import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import progressGif from "../images/progress.gif";
import * as XLSX from "xlsx";

const Date_Wise_Sale_Dist = () => {
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [schemes, setSchemes] = useState([]);
  const [commodityScheme, setCommodityScheme] = useState([]);
  const [saleSummary, setSaleSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedMonth, setSubmittedMonth] = useState("");
  const [submittedYear, setSubmittedYear] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;



  const submittedMonthName =
    months.find((m) => m.month === parseInt(submittedMonth))?.name || "";

  useEffect(() => {
    setSelectedMonth(currentMonth.toString());
    setSelectedYear(currentYear.toString());

    setMonths([
      { month: 1, name: "January" },
      { month: 2, name: "February" },
      { month: 3, name: "March" },
      { month: 4, name: "April" },
      { month: 5, name: "May" },
      { month: 6, name: "June" },
      { month: 7, name: "July" },
      { month: 8, name: "August" },
      { month: 9, name: "September" },
      { month: 10, name: "October" },
      { month: 11, name: "November" },
      { month: 12, name: "December" },
    ]);

    const yearArray = [];
    for (let y = 2019; y <= currentYear; y++) yearArray.push(y);
    setYears(yearArray);
  }, [currentMonth, currentYear]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMonth || !selectedYear) {
      alert("Please select month & year");
      return;
    }

    setSubmittedMonth(selectedMonth);
    setSubmittedYear(selectedYear);
    setLoading(true);
    setSubmitted(true);
    setSaleSummary([]);
    setSchemes([]);
    setCommodityScheme([]);
    setLastUpdated("");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL_SALE}/api/dateWiseSaleDist/commoditySchemeData`,
        { params: { month: selectedMonth, year: selectedYear } }
      );

      const data = res.data || {};
      setSchemes(data.schemes || []);
      setCommodityScheme(data.commoditySchemes || []);

      const summary = (data.dateWiseSaleSummary || []).map((row) => {
        const dyn = row.dynamicFields || {};
        const mappedDyn = {};

        (data.schemes || []).forEach((s) => {
          mappedDyn[`cards_${s.schemeId}`] = dyn[`scheme${s.schemeId}cards`] || 0;
          mappedDyn[`trans_${s.schemeId}`] = dyn[`scheme${s.schemeId}trans`] || 0;
        });

        (data.commoditySchemes || []).forEach((cs) => {
          mappedDyn[`availed_${cs.commId}_${cs.schemeId}`] =
            dyn[`scheme${cs.schemeId}_comm${cs.commId}qty`] || 0;
        });

        const uniqueComms = [...new Set(data.commoditySchemes.map((cs) => cs.commId))];
        uniqueComms.forEach((commId) => {
          mappedDyn[`totalqty_${commId}`] = dyn[`comm${commId}totalqty`] || 0;
        });

        mappedDyn.transDate = row.transDate;
        mappedDyn.dataTime = row.dataTime;

        return { ...row, dynamicFields: mappedDyn };
      });

      setSaleSummary(summary);

      const now = new Date();
      setLastUpdated(
        `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
          now.getDate()
        ).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(
          now.getMinutes()
        ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`
      );
    } catch (err) {
      console.error(err);
      alert("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedMonth(currentMonth.toString());
    setSelectedYear(currentYear.toString());
    setSchemes([]);
    setCommodityScheme([]);
    setSaleSummary([]);
    setSubmitted(false);
    setLoading(false);
    setSubmittedMonth("");
    setSubmittedYear("");
    setLastUpdated("");
  };

  const uniqueCommodities = useMemo(() => {
    return [...new Map(commodityScheme.map((cs) => [cs.commId, cs])).values()];
  }, [commodityScheme]);

  const totalRow = useMemo(() => {
    const totals = { dynamicFields: {} };
    saleSummary.forEach((row) => {
      const dyn = row.dynamicFields || {};
      Object.keys(dyn).forEach((k) => {
        if (k !== "transDate" && k !== "dataTime") {
          totals.dynamicFields[k] = (totals.dynamicFields[k] || 0) + Number(dyn[k] || 0);
        }
      });
    });
    return totals;
  }, [saleSummary]);

  const formatDate = (dateString) => (dateString ? dateString.substring(0, 10) : "");

  const exportToExcel = () => {
    if (saleSummary.length === 0) return;

    const excelData = [];
    excelData.push([`Date Wise Sale for ${submittedMonthName} - ${submittedYear}`]);

    const headerRow = ["Sl.No", "Date"];
    schemes.forEach((s) => headerRow.push(`Cards ${s.schemeNameEn}`));
    schemes.forEach((s) => headerRow.push(`Trans ${s.schemeNameEn}`));
    headerRow.push("Total Cards", "Total Trans");
    commodityScheme.forEach((cs) =>
      headerRow.push(`${cs.commNameEn} (${cs.unitType})`)
    );
    uniqueCommodities.forEach((uc) =>
      headerRow.push(`${uc.commNameEn} (${uc.unitType})`)
    );
    excelData.push(headerRow);

    saleSummary.forEach((row, index) => {
      const dyn = row.dynamicFields || {};
      const dataRow = [index + 1, formatDate(dyn.transDate)];
      schemes.forEach((s) => dataRow.push(Number(dyn[`cards_${s.schemeId}`] || 0)));
      schemes.forEach((s) => dataRow.push(Number(dyn[`trans_${s.schemeId}`] || 0)));
      dataRow.push(
        schemes.reduce((sum, s) => sum + Number(dyn[`cards_${s.schemeId}`] || 0), 0)
      );
      dataRow.push(
        schemes.reduce((sum, s) => sum + Number(dyn[`trans_${s.schemeId}`] || 0), 0)
      );
      commodityScheme.forEach((cs) =>
        dataRow.push(Number(dyn[`availed_${cs.commId}_${cs.schemeId}`] || 0))
      );
      uniqueCommodities.forEach((uc) =>
        dataRow.push(Number(dyn[`totalqty_${uc.commId}`] || 0))
      );
      excelData.push(dataRow);
    });

    const totalExcelRow = ["Total", ""];
    schemes.forEach((s) =>
      totalExcelRow.push(Number(totalRow.dynamicFields[`cards_${s.schemeId}`] || 0))
    );
    schemes.forEach((s) =>
      totalExcelRow.push(Number(totalRow.dynamicFields[`trans_${s.schemeId}`] || 0))
    );
    totalExcelRow.push(
      schemes.reduce((sum, s) => sum + Number(totalRow.dynamicFields[`cards_${s.schemeId}`] || 0), 0)
    );
    totalExcelRow.push(
      schemes.reduce((sum, s) => sum + Number(totalRow.dynamicFields[`trans_${s.schemeId}`] || 0), 0)
    );
    commodityScheme.forEach((cs) =>
      totalExcelRow.push(Number(totalRow.dynamicFields[`availed_${cs.commId}_${cs.schemeId}`] || 0))
    );
    uniqueCommodities.forEach((uc) =>
      totalExcelRow.push(Number(totalRow.dynamicFields[`totalqty_${uc.commId}`] || 0))
    );
    excelData.push(totalExcelRow);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    XLSX.utils.book_append_sheet(wb, ws, "DateWiseSale");
    XLSX.writeFile(wb, `DateWiseSale_${submittedMonthName}_${submittedYear}.xlsx`);
  };

  return (
    <div style={{ padding: "10px", marginBottom: "10px" }}>
      {/* Form */}
      <div style={{ width: "700px", margin: "0 auto", fontSize: "10px" }}>

        <table >
          <tr className="tableheader">
            <th colSpan={4}>Date Wise Sale</th>

          </tr>
          <tbody>
            <tr>
              <td style={{ fontWeight: "bold", padding: "10px" }}>Distribution Month</td>
              <td style={{ padding: "10px" }}>
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  style={{ fontSize: "16px", padding: "5px 10px" }}
                >
                  <option value="">Select Month</option>
                  {months.map((m) => (
                    <option key={m.month} value={m.month}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ fontWeight: "bold", padding: "10px" }}>Distribution Year</td>
              <td style={{ padding: "10px" }}>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  style={{ fontSize: "16px", padding: "5px 10px" }}
                >
                  <option value="">Select Year</option>
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td colSpan="4" style={{ padding: "3px", textAlign: "center" }}>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{
                    backgroundColor: "#1c1c1c",
                    color: "#fff",
                    border: "none",
                    padding: "5px 20px",
                    fontSize: "12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                    marginRight: "5px",
                  }}
                >
                  Submit
                </button>
                <button
                  onClick={handleBack}
                  style={{
                    backgroundColor: "#1c1c1c",
                    color: "#fff",
                    border: "none",
                    padding: "5px 20px",
                    fontSize: "12px",
                    borderRadius: "12px",
                    cursor: "pointer",
                  }}
                >
                  Back
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {loading && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <img src={progressGif} alt="Loading..." width="250" height="15" />
        </div>
      )}

      {!loading && submitted && saleSummary.length === 0 && (
        <p
          style={{
            color: "red",
            textAlign: "center",
            fontSize: "20px",
            marginTop: "20px",
          }}
        >
          No data found for {submittedMonthName} - {submittedYear}
        </p>
      )}

      {!loading && saleSummary.length > 0 && (
        <div style={{ width: "100%", marginTop: "50px", paddingBottom: "100px" }}>
          <div style={{ textAlign: "right", marginBottom: "10px" }}>
            <button
              onClick={exportToExcel}
              style={{
                backgroundColor: "#1c1c1c",
                color: "#fff",
                border: "none",
                padding: "8px 20px",
                fontSize: "15px",
                borderRadius: "12px",
                cursor: "pointer",
              }}
            >
              Export to Excel
            </button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table align="center">
              <thead>
                <tr className="tableheader">
                  <th
                    colSpan={
                      2 + schemes.length * 2 + 2 + commodityScheme.length + uniqueCommodities.length
                    }

                  >
                    Date Wise Sale for <span style={{ color: "orange" }}>{submittedMonthName}</span> -{" "}
                    <span style={{ color: "orange" }}>{submittedYear}</span>
                  </th>
                </tr>
                <tr className="tableheader">
                  <th rowSpan="3" >
                    Sl.No
                  </th>
                  <th rowSpan="3" >
                    Date
                  </th>
                  <th colSpan={schemes.length} rowSpan="2">
                    No of Cards Availed
                  </th>
                  <th colSpan={schemes.length} rowSpan="2" >
                    No of Transactions
                  </th>
                  <th colSpan="2" rowSpan="2" >
                    Total
                  </th>
                  <th colSpan={commodityScheme.length} >
                    Quantity Distributed
                  </th>
                  <th colSpan={uniqueCommodities.length} rowSpan="2" >
                    Total Distributed
                  </th>
                </tr>
                <tr className="tableheader">
                  {commodityScheme.map((cs, idx) => (
                    <th key={idx + "_commName_row2"} >
                      {cs.schemeNameEn}
                    </th>
                  ))}
                </tr>
                <tr className="tableheader">
                  {schemes.map((s) => (
                    <th key={s.schemeId + "_cards"} >
                      {s.schemeNameEn}
                    </th>
                  ))}
                  {schemes.map((s) => (
                    <th key={s.schemeId + "_trans"} >
                      {s.schemeNameEn}
                    </th>
                  ))}
                  <th >Cards</th>
                  <th >Transactions</th>
                  {commodityScheme.map((cs) => (
                    <th key={cs.commId + "_" + cs.schemeId} >
                      {cs.commNameEn} <br /> ({cs.unitType})
                    </th>
                  ))}
                  {uniqueCommodities.map((uc) => (
                    <th key={uc.commId + "_total_units_row3"} >
                      {uc.commNameEn} <br /> ({uc.unitType})
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {saleSummary.map((row, index) => {
                  const dyn = row.dynamicFields || {};
                  return (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{formatDate(dyn.transDate)}</td>
                      {schemes.map((s) => (
                        <td key={s.schemeId + "_cards_val"}>{Number(dyn[`cards_${s.schemeId}`] || 0)}</td>
                      ))}
                      {schemes.map((s) => (
                        <td key={s.schemeId + "_trans_val"}>{Number(dyn[`trans_${s.schemeId}`] || 0)}</td>
                      ))}
                      <td>
                        {schemes.reduce((sum, s) => sum + Number(dyn[`cards_${s.schemeId}`] || 0), 0)}
                      </td>
                      <td>
                        {schemes.reduce((sum, s) => sum + Number(dyn[`trans_${s.schemeId}`] || 0), 0)}
                      </td>
                      {commodityScheme.map((cs) => (
                        <td key={cs.commId + "_" + cs.schemeId + "_val"}>
                          {Number(dyn[`availed_${cs.commId}_${cs.schemeId}`] || 0).toFixed(3)}
                        </td>
                      ))}
                      {uniqueCommodities.map((uc) => (
                        <td key={uc.commId + "_total_val"}>
                          {Number(dyn[`totalqty_${uc.commId}`] || 0).toFixed(3)}
                        </td>
                      ))}
                    </tr>
                  );
                })}

                <tr className="tableheader">
                  <td colSpan="2">Total</td>
                  {schemes.map((s) => (
                    <td key={s.schemeId + "_cards_total"}>{Number(totalRow.dynamicFields[`cards_${s.schemeId}`] || 0)}</td>
                  ))}
                  {schemes.map((s) => (
                    <td key={s.schemeId + "_trans_total"}>{Number(totalRow.dynamicFields[`trans_${s.schemeId}`] || 0)}</td>
                  ))}
                  <td>
                    {schemes.reduce((sum, s) => sum + Number(totalRow.dynamicFields[`cards_${s.schemeId}`] || 0), 0)}
                  </td>
                  <td>
                    {schemes.reduce((sum, s) => sum + Number(totalRow.dynamicFields[`trans_${s.schemeId}`] || 0), 0)}
                  </td>
                  {commodityScheme.map((cs) => (
                    <td key={cs.commId + "_" + cs.schemeId + "_total"}>
                      {Number(totalRow.dynamicFields[`availed_${cs.commId}_${cs.schemeId}`] || 0).toFixed(3)}
                    </td>
                  ))}
                  {uniqueCommodities.map((uc) => (
                    <td key={uc.commId + "_grand_total"}>
                      {Number(totalRow.dynamicFields[`totalqty_${uc.commId}`] || 0).toFixed(3)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {lastUpdated && (
            <div style={{ width: "100%", marginTop: "8px", textAlign: "right", paddingRight: "5px" }}>
              <span style={{ color: "green", fontWeight: "bold" }}>Data Time: </span>
              <span style={{ color: "red", fontWeight: "bold" }}>{lastUpdated}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Date_Wise_Sale_Dist;
